import { useStore } from '@nanostores/react';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { AnimatePresence, motion } from 'framer-motion';
import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { AddBranchDialog } from '~/components/github/AddBranchDialog';
import { GithubStatusPopover } from '~/components/github/GithubStatusPopover';
import { LegacyIconButton } from '~/components/ui/LegacyIconButton';
import { sendAnalyticsEvent } from '~/lib/analytics.client';
import { navigateToBranch, useGithubToken } from '~/lib/client-github';
import { useGithubStore } from '~/lib/context/github';
import {
  createRef,
  getBranches,
  getReference,
  getRepositoryId,
  getUserOrOrganization,
  GraphQLError,
} from '~/lib/github-api';
import { userStore } from '~/lib/stores/user';
import { AnalyticsAction, AnalyticsTrackEvent } from '~/types/analytics';
import type { UserData } from '~/types/user';
import { dropdownAnimationVariants } from '~/utils/animationVariants';
import { ClientError } from '~/utils/client-error';
import { invariant } from '~/utils/invariant';
import { logger } from '~/utils/logger';
import { wait } from '~/utils/promises';

export function GithubButton() {
  const github = useGithubStore();
  const username = useStore(github.username);
  const githubState = useStore(github.state);

  const [creating, setCreating] = useState(false);
  const user = useStore(userStore.user) as UserData;
  const [branches, setBranches] = useState<string[]>();
  const [avatarUrl, setAvatarUrl] = useState<string>();

  if (import.meta.env.DEV) {
    invariant(user);
  }

  useEffect(() => {
    retrieveBranches();
  }, []);

  const getGithubToken = useGithubToken();

  useEffect(() => {
    async function retrieveAvatar() {
      if (!githubState.integration?.repository) {
        return;
      }

      const { repository } = githubState.integration;

      if (repository.owner === username) {
        setAvatarUrl(undefined);

        return;
      }

      setAvatarUrl('');

      const token = await getGithubToken();

      if (!token) {
        return;
      }

      const userData = await getUserOrOrganization(repository.owner, token);

      if (userData) {
        setAvatarUrl(userData.avatarUrl);
      }
    }

    retrieveAvatar();
  }, [githubState.integration?.repository.owner, username]);

  async function retrieveBranches() {
    if (!(username && githubState.integration)) {
      return;
    }

    const token = await getGithubToken();

    if (!token) {
      return;
    }

    const branches = await getBranches(githubState.integration, token);

    setBranches(branches);
  }

  function dismiss() {
    github.changeDropdownVisibility(false);
  }

  function startCreateNewBranch() {
    setCreating(true);
  }

  async function createNewBranch(branch: string) {
    try {
      await tryCreateNewBranch(branch);
    } catch (error: any) {
      logger.error(error);

      if (error instanceof Error && error.cause instanceof GraphQLError) {
        const unprocessable = error.cause.errors.find(({ type }) => type === 'UNPROCESSABLE');

        if (unprocessable) {
          return { message: unprocessable.message };
        }
      }

      toast.error(error instanceof ClientError ? error.message : 'Unable to create branch');
    }

    return undefined;
  }

  async function tryCreateNewBranch(branch: string) {
    if (!(githubState.integration && username)) {
      return;
    }

    const token = await getGithubToken();

    if (!token) {
      throw new Error('Unexpected missing credentials');
    }

    const [ref, repositoryId] = await Promise.all([
      getReference(githubState.integration, token),
      getRepositoryId(githubState.integration.repository, token),
    ]);

    if (!ref?.target?.oid) {
      throw new Error('Unexpected missing oid');
    }

    try {
      await createRef({ branchName: branch, oid: ref.target.oid, repositoryId }, token);
    } catch (error) {
      if (error instanceof GraphQLError) {
        throw new ClientError(error.errors.at(0)?.message ?? 'Unable to create branch', { cause: error });
      }

      throw error;
    }

    sendAnalyticsEvent({
      action: AnalyticsAction.Track,
      payload: {
        event: AnalyticsTrackEvent.GithubBranchCreated,
        properties: {
          repositoryOwner: githubState.integration.repository.owner,
          repositoryName: githubState.integration.repository.name,
          branchName: branch,
        },
      },
    });

    navigateToBranch(branch);

    // don't signal we're ready immediately
    await wait(3_000);
  }

  function dimissNewBranch() {
    dismiss();
    setCreating(false);
  }

  const disconnected = githubState.connected === false;

  return (
    <DropdownMenu.Root
      open={githubState.isDropdownVisible && !creating && !disconnected}
      onOpenChange={github.changeDropdownVisibility}
    >
      <DropdownMenu.Trigger asChild>
        <LegacyIconButton className="relative size-8" size="sm">
          <div className="i-bx:bxl-github size-8 text-bolt-elements-textPrimary" />
        </LegacyIconButton>
      </DropdownMenu.Trigger>
      <AnimatePresence>
        {githubState.isDropdownVisible && (
          <DropdownMenu.Portal forceMount>
            <DropdownMenu.Content
              className="z-visibility-dropdown"
              asChild
              forceMount
              avoidCollisions={false}
              sideOffset={3}
              align="end"
            >
              <motion.div initial="close" animate="open" exit="close" variants={dropdownAnimationVariants}>
                {!creating && username && (
                  <GithubStatusPopover
                    status={githubState.status}
                    branches={branches}
                    branchName={githubState.integration!.branchName}
                    repository={githubState.integration!.repository}
                    onSwitch={navigateToBranch}
                    onCreate={startCreateNewBranch}
                  />
                )}
                {creating && username && (
                  <AddBranchDialog
                    currentBranch={githubState.integration!.branchName}
                    repository={githubState.integration!.repository}
                    avatarUrl={avatarUrl != null ? avatarUrl : user.avatar}
                    open
                    onClose={dimissNewBranch}
                    onChoose={createNewBranch}
                    username={username}
                  />
                )}

                <DropdownMenu.Arrow className="fill-bolt-elements-borderColor" />
              </motion.div>
            </DropdownMenu.Content>
          </DropdownMenu.Portal>
        )}
      </AnimatePresence>
    </DropdownMenu.Root>
  );
}
